import { ApiConfig, handleResponse } from './config';
import { ModuleProgress } from '../../types/module';

export class ProgressService {
  constructor(private config: ApiConfig) {}

  // Get current auth headers with fresh token
  private getAuthHeaders(): Record<string, string> {
    const headers = { ...this.config.headers };
    
    // Try to get fresh token from auth store
    if (typeof window !== 'undefined') {
      try {
        const authStorage = JSON.parse(localStorage.getItem('auth-storage') || '{}');
        const token = authStorage.state?.token;
        if (token && token !== 'null' && token !== 'undefined' && token !== '') {
          headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // Keep existing headers if we can't get fresh token
      }
    }
    
    return headers;
  }

  async getProgress(): Promise<ModuleProgress[]> {
    // This assumes the current user's progress will be fetched
    // The server extracts the userId from the auth token
    const headers = this.getAuthHeaders();
    console.log('Fetching progress for current user');
    console.log('Headers being sent:', headers);
    
    // Check if we have a valid Authorization header before making the request
    const authHeader = headers.Authorization;
    if (!authHeader || authHeader === 'Bearer null' || authHeader === 'Bearer undefined' || authHeader === 'Bearer ') {
      console.log('No valid auth token available - returning empty progress without making request');
      return [];
    }
    
    try {
      const response = await fetch(`${this.config.baseUrl}/progress`, {
        headers
      });

      console.log('Progress fetch response:', {
        status: response.status,
        statusText: response.statusText,
        url: response.url
      });

      // Handle authentication errors gracefully
      if (response.status === 401) {
        console.log('Not authenticated - returning empty progress');
        return [];
      }

      return handleResponse(response);
    } catch (error) {
      console.log('Error fetching progress, returning empty array:', error);
      return [];
    }
  }

  async getUserProgress(userId: string): Promise<ModuleProgress[]> {
    console.log(`Fetching progress for user: ${userId}`);
    const headers = this.getAuthHeaders();
    const response = await fetch(`${this.config.baseUrl}/progress/${userId}`, {
      headers
    });

    return handleResponse(response);
  }

  async updateProgress(
    userId: string,
    moduleId: string,
    contentId: string,
    update: {
      completed: boolean;
      lastAccessed?: Date;
      quizScore?: number;
      exerciseSubmission?: string;
    }
  ): Promise<ModuleProgress> {
    const headers = this.getAuthHeaders();
    console.log(`🚀 Starting progress update API call:`, {
      userId,
      moduleId,
      contentId,
      update,
      url: `${this.config.baseUrl}/progress/${userId}/${moduleId}`,
      headers
    });
    
    try {
      const response = await fetch(`${this.config.baseUrl}/progress/${userId}/${moduleId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          contentId,
          ...update
        })
      });

      console.log(`📡 Progress update response:`, {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Progress update failed:`, {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await handleResponse(response);
      
      // Handle Map serialization - convert Map back to Object if needed
      if (result && result.contentProgress) {
        // If contentProgress is an array of key-value pairs (Map serialization)
        if (Array.isArray(result.contentProgress)) {
          result.contentProgress = Object.fromEntries(result.contentProgress);
        }
        // If contentProgress is already an object, keep it as is
      }
      
      console.log('✅ Progress update response processed successfully:', {
        userId,
        moduleId,
        contentId,
        contentProgressType: typeof result.contentProgress,
        contentProgressKeys: result.contentProgress ? Object.keys(result.contentProgress) : [],
        result
      });
      
      return result;
    } catch (error) {
      console.error('❌ Progress update API call failed:', error);
      throw error;
    }
  }

  async getModuleProgress(userId: string, moduleId: string): Promise<ModuleProgress> {
    console.log(`Fetching progress for module: ${moduleId}`);
    const headers = this.getAuthHeaders();
    const response = await fetch(`${this.config.baseUrl}/progress/${userId}/${moduleId}`, {
      headers
    });

    return handleResponse(response);
  }
}